import { c as _c } from "react/compiler-runtime";
import { createColumnHelper } from '@tanstack/react-table';
import type { SubtitleResponseData } from '@/app/[locale]/subtitles/_api/os';
import { Div } from '@/components/ui/html-elements';
import { Separator } from '@/components/ui/separator';
import { HeaderTitle, TableDataCell, TableHeaderCell } from '@/components/ui/table-element';
import { useI18n } from '@/locales/client';
import { customFormatDistance, customFormatDistanceToNowStrict, formatDistanceLocale } from '@sub-vocab/utils/lib';
type Subtitle = {
  subtitle: SubtitleResponseData;
};
export type SubtitleData<T = undefined> = T extends undefined ? Subtitle : Subtitle & {
  media?: T | undefined;
};
const displayNames = new Intl.DisplayNames(['en'], {
  type: 'language'
});
const numberFormat = new Intl.NumberFormat('en');
export function useCommonColumns(rootRef) {
  const $ = _c(8);
  useI18n();
  let t0;
  let t1;
  let t2;
  if ($[0] !== rootRef?.current) {
    const columnHelper = createColumnHelper();
    t0 = columnHelper.accessor(_temp, {
      id: "language",
      header: t3 => {
        const {
          header
        } = t3;
        const isSorted = header.column.getIsSorted();
        return <TableHeaderCell header={header} className="w-[.1%] active:bg-background-active active:signal/active [&:active+th]:signal/active"><Div className="group gap-2 pr-1 select-none" onClick={e => {
            header.column.getToggleSortingHandler()?.(e);
            requestAnimationFrame(() => rootRef?.current?.scrollTo({
              top: 0
            }));
          }}><Separator orientation="vertical" className="h-5 signal/active:h-full" /><HeaderTitle title="Language" isSorted={isSorted} className="*:data-title:text-right" /></Div></TableHeaderCell>;
      },
      cell: _temp2
    });
    t1 = columnHelper.accessor(_temp3, {
      id: "download_count",
      header: t4 => {
        const {
          header: header_0
        } = t4;
        const isSorted_0 = header_0.column.getIsSorted();
        return <TableHeaderCell header={header_0} className="w-[.1%] active:bg-background-active active:signal/active [&:active+th]:signal/active"><Div className="group items-center gap-2 pr-1 select-none" onClick={e_0 => {
            header_0.column.getToggleSortingHandler()?.(e_0);
            requestAnimationFrame(() => rootRef?.current?.scrollTo({
              top: 0
            }));
          }}><Separator orientation="vertical" className="h-5 signal/active:h-full" /><HeaderTitle title="Downloads" isSorted={isSorted_0} className="*:data-title:text-right" /></Div></TableHeaderCell>;
      },
      cell: _temp4
    });
    t2 = columnHelper.accessor(_temp5, {
      id: "upload_date",
      header: t5 => {
        const {
          header: header_1
        } = t5;
        const isSorted_1 = header_1.column.getIsSorted();
        return <TableHeaderCell header={header_1} className="w-[.1%] active:bg-background-active active:signal/active [&:active+th]:signal/active"><Div className="group gap-2 pr-1 select-none" onClick={e_1 => {
            header_1.column.getToggleSortingHandler()?.(e_1);
            requestAnimationFrame(() => rootRef?.current?.scrollTo({
              top: 0
            }));
          }}><Separator orientation="vertical" className="h-5 signal/active:h-full" /><HeaderTitle title="Uploaded" isSorted={isSorted_1} className="*:data-title:text-left" /></Div></TableHeaderCell>;
      },
      cell: _temp6
    });
    $[0] = rootRef?.current;
    $[1] = t0;
    $[2] = t1;
    $[3] = t2;
  } else {
    t0 = $[1];
    t1 = $[2];
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t0 || $[5] !== t1 || $[6] !== t2) {
    t3 = [t0, t1, t2];
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function _temp6(t0) {
  const {
    cell: cell_1,
    getValue: getValue_1
  } = t0;
  const value_1 = getValue_1();
  return <TableDataCell cell={cell_1}><Div className="justify-end pr-1.5 tracking-[.03em] tabular-nums">{value_1 ? customFormatDistanceToNowStrict(new Date(value_1), {
        addSuffix: true,
        locale: {
          formatDistance: customFormatDistance(formatDistanceLocale)
        }
      }) : null}</Div></TableDataCell>;
}
function _temp5(row_1) {
  return row_1.subtitle.attributes.upload_date;
}
function _temp4(t0) {
  const {
    cell: cell_0,
    getValue: getValue_0
  } = t0;
  const value_0 = getValue_0();
  return <TableDataCell cell={cell_0}><Div className="justify-end pr-4 pl-0.5 text-xs tracking-[.03em] tabular-nums">{numberFormat.format(value_0)}</Div></TableDataCell>;
}
function _temp3(row_0) {
  return row_0.subtitle.attributes.download_count || 0;
}
function _temp2(t0) {
  const {
    cell,
    getValue
  } = t0;
  const value = getValue();
  const displayName = displayNames.of(value);
  return <TableDataCell cell={cell}><Div className="justify-center pr-px pl-1.5 tracking-[.03em] whitespace-nowrap tabular-nums"><span>{displayName}</span></Div></TableDataCell>;
}
function _temp(row) {
  return row.subtitle.attributes.language;
}